import { useMemo } from 'react';
import OddsCell from './OddsCell';
import { formatPrice, formatSpread, formatTotal } from '../../utils/oddsFormat';
import { getBookmakerColumns } from '../../utils/bookmakers';

export default function OddsTable({ events, market, oddsFormat, onEventClick, favoriteTeams = [] }) {
  const favSet = useMemo(() => new Set(favoriteTeams.map((t) => t.toLowerCase())), [favoriteTeams]);
  const columns = useMemo(() => getBookmakerColumns(events), [events]);

  return (
    <div className="overflow-x-auto rounded border border-[#E5E7EB] dark:border-[#1e2536]">
      <table className="w-full" style={{ fontFamily: 'var(--font-mono)' }}>
        <thead>
          <tr className="bg-[#F8F9FA] dark:bg-[#0a0d14]">
            <th className="text-left px-3 py-2.5 text-[12px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#505a6e] min-w-[180px] sticky left-0 bg-[#F8F9FA] dark:bg-[#0a0d14] z-10" style={{ fontFamily: 'var(--font-sans)' }}>
              Game
            </th>
            {columns.map((col) => (
              <th key={col.key} className="text-center px-2 py-2.5 text-[12px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#505a6e] min-w-[110px] border-l border-[#E5E7EB] dark:border-[#1e2536]" style={{ fontFamily: 'var(--font-sans)' }}>
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((event, i) => (
            <EventRow
              key={event.id}
              event={event}
              columns={columns}
              market={market}
              oddsFormat={oddsFormat}
              onClick={() => onEventClick(event.id)}
              even={i % 2 === 0}
              isFavorite={favSet.has(event.home_team?.toLowerCase()) || favSet.has(event.away_team?.toLowerCase())}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventRow({ event, columns, market, oddsFormat, onClick, even, isFavorite }) {
  const gameTime = new Date(event.commence_time).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const bookmakerOdds = {};
  for (const bm of event.bookmakers || []) {
    const mkt = bm.markets?.find((m) => m.key === market);
    if (mkt) {
      bookmakerOdds[bm.key] = {
        outcomes: mkt.outcomes,
        homeOutcome: mkt.outcomes.find((o) => o.name === event.home_team),
        awayOutcome: mkt.outcomes.find((o) => o.name === event.away_team),
        overOutcome: mkt.outcomes.find((o) => o.name === 'Over'),
        underOutcome: mkt.outcomes.find((o) => o.name === 'Under'),
      };
    }
  }

  const best = findBestOdds(bookmakerOdds, market);
  const rowBg = even
    ? 'bg-white dark:bg-[#151a25]'
    : 'bg-[#F8F9FA] dark:bg-[#1a2030]';

  return (
    <tr
      onClick={onClick}
      className={`${rowBg} hover:bg-[#eff6ff] dark:hover:bg-[#1e2a40] cursor-pointer transition-colors duration-150`}
      style={isFavorite ? { boxShadow: 'inset 3px 0 0 #005BB3' } : undefined}
    >
      <td className={`px-3 py-2.5 sticky left-0 z-10 ${rowBg} border-t border-[#E5E7EB] dark:border-[#1e2536]`}>
        <div className="flex flex-col" style={{ fontFamily: 'var(--font-sans)' }}>
          <span className="text-[14px] font-medium text-[#111827] dark:text-[#e2e8f0] leading-tight">{event.away_team}</span>
          <span className="text-[14px] font-medium text-[#111827] dark:text-[#e2e8f0] leading-tight">
            <span className="text-[#6B7280] dark:text-[#505a6e] font-normal">@</span> {event.home_team}
          </span>
          <span className="text-[12px] text-[#6B7280] dark:text-[#505a6e] mt-0.5">{gameTime}</span>
        </div>
      </td>

      {columns.map((col) => {
        const data = bookmakerOdds[col.key];
        if (!data) {
          return (
            <td key={col.key} className="text-center px-2 py-2.5 text-[#D1D5DB] dark:text-[#2a3348] border-l border-t border-[#E5E7EB] dark:border-[#1e2536]">—</td>
          );
        }
        return (
          <td key={col.key} className="text-center px-2 py-2.5 border-l border-t border-[#E5E7EB] dark:border-[#1e2536]">
            <CellContent data={data} market={market} oddsFormat={oddsFormat} best={best} bk={col.key} />
          </td>
        );
      })}
    </tr>
  );
}

function CellContent({ data, market, oddsFormat, best, bk }) {
  if (market === 'spreads') {
    return (
      <div className="flex flex-col gap-px">
        <OddsCell text={data.homeOutcome ? formatSpread(data.homeOutcome.point, data.homeOutcome.price, oddsFormat) : '—'} highlight={best.homeBest === bk ? 'best' : best.homeWorst === bk ? 'worst' : null} />
        <OddsCell text={data.awayOutcome ? formatSpread(data.awayOutcome.point, data.awayOutcome.price, oddsFormat) : '—'} highlight={best.awayBest === bk ? 'best' : best.awayWorst === bk ? 'worst' : null} />
      </div>
    );
  }
  if (market === 'h2h') {
    return (
      <div className="flex flex-col gap-px">
        <OddsCell text={data.homeOutcome ? formatPrice(data.homeOutcome.price, oddsFormat) : '—'} highlight={best.homeBest === bk ? 'best' : best.homeWorst === bk ? 'worst' : null} />
        <OddsCell text={data.awayOutcome ? formatPrice(data.awayOutcome.price, oddsFormat) : '—'} highlight={best.awayBest === bk ? 'best' : best.awayWorst === bk ? 'worst' : null} />
      </div>
    );
  }
  if (market === 'totals') {
    return (
      <div className="flex flex-col gap-px">
        <OddsCell text={data.overOutcome ? formatTotal('O', data.overOutcome.point, data.overOutcome.price, oddsFormat) : '—'} highlight={best.overBest === bk ? 'best' : best.overWorst === bk ? 'worst' : null} />
        <OddsCell text={data.underOutcome ? formatTotal('U', data.underOutcome.point, data.underOutcome.price, oddsFormat) : '—'} highlight={best.underBest === bk ? 'best' : best.underWorst === bk ? 'worst' : null} />
      </div>
    );
  }
  return null;
}

function findBestOdds(bookmakerOdds, market) {
  const result = {};
  const sides = market === 'totals'
    ? [['overOutcome', 'overBest', 'overWorst'], ['underOutcome', 'underBest', 'underWorst']]
    : [['homeOutcome', 'homeBest', 'homeWorst'], ['awayOutcome', 'awayBest', 'awayWorst']];

  for (const [sideKey, bestKey, worstKey] of sides) {
    let best = { bk: null, price: -Infinity };
    let worst = { bk: null, price: Infinity };
    for (const [bk, data] of Object.entries(bookmakerOdds)) {
      const o = data[sideKey];
      if (o) {
        if (o.price > best.price) best = { bk, price: o.price };
        if (o.price < worst.price) worst = { bk, price: o.price };
      }
    }
    result[bestKey] = best.bk;
    result[worstKey] = worst.bk;
  }
  return result;
}
